import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { CreditCard, Lock, Info } from 'lucide-react';
import { StepHelpers } from '@shared/components/Stepper';
import { Button } from '@shared/ui/button';
import { useCurrency } from '@shared/selectors/globalSelectors';
import { SelectedAccountsBanner } from '../../../../../shared/components/SelectedAccountsBanner';
import { RadioGroup, RadioGroupItem } from'@shared/ui/radio-group';
import { useUtilityRequestConfiguration } from '../../../../../shared/hook';
import { useCreateSingleServiceRequests, useCreateBulkServiceRequests } from '../../../hooks';
import { getLoginDataFromStorage } from '@shared/utils/loginUtils';
import { getRemoteUtilityId } from '@shared/utils/getUtilityId';
import { useTranslation } from 'react-i18next';

export const ReviewPaymentStep = ({
 stepHelpers,
 currentStepIndex = 2,
 onNext,
 onPrevious,
 remoteUtilityId = getRemoteUtilityId(),
 requestType = "Service"
}: {
 stepHelpers?: StepHelpers;
 currentStepIndex?: number;
 onNext?: () => void;
 onPrevious?: () => void;
 remoteUtilityId?: number;
 requestType?: string;
}) => {
 const { t } = useTranslation();
 const location = useLocation();
 const searchParams = new URLSearchParams(location.search);
 const isSubAccount = searchParams.get('isSubAccount') === 'true';
 const urlConsumerId = searchParams.get('consumerId');
 const [paymentMethod, setPaymentMethod] = useState('online');
 const currency = useCurrency();

 const selectedId = stepHelpers?.getStepData(currentStepIndex - 2)?.selectedId;
 const { data: configData, isLoading } = useUtilityRequestConfiguration({
  remote_utility_id: remoteUtilityId,
  requestType: requestType,
  disablePagination: true,
 });

 const selectedService = (configData?.result || []).find(s => s.id?.toString() === selectedId);
 const category = selectedService?.supportRequestTypeDisplay || selectedService?.requestTypeDisplay || 'Service';
 
 let utilityServices: string[] = [];
 if (Array.isArray(selectedService?.productCode)) {
  utilityServices = selectedService.productCode;
 } else if (selectedService?.productCode) {
  utilityServices = [selectedService.productCode];
 }

 const rawPrice = selectedService?.extraData?.serviceCharge;
 const isFree = rawPrice === 0 || rawPrice === '0' || rawPrice === '0.00' || !rawPrice;
 const priceDisplay = isFree ? <span className="text-green-600 font-bold uppercase">{t("FREE")}</span> : <>{currency} {rawPrice}</>;
 
 const step1Data = stepHelpers?.getStepData(currentStepIndex - 1) || {};
 const preferredDate = step1Data.preferredDate || t("Not specified");
 const preferredTime = step1Data.preferredTime || t("Not specified");

 useEffect(() => {
 if (stepHelpers) {
  const data = stepHelpers.getStepData(currentStepIndex);
  if (data && data.paymentMethod) {
  setPaymentMethod(data.paymentMethod);
  }
 }
 }, [stepHelpers, currentStepIndex]);

 const updatePaymentMethod = (method: string) => {
 setPaymentMethod(method);
 if (stepHelpers) {
  stepHelpers.setStepData(currentStepIndex, { paymentMethod: method });
 }
 };

 const { mutate: createSingleServiceRequest, isPending: isSinglePending } = useCreateSingleServiceRequests();
 const { mutate: createBulkServiceRequest, isPending: isBulkPending } = useCreateBulkServiceRequests();
 const isSubmitting = isSinglePending || isBulkPending;

 const handleSubmit = () => {
    const isBulk = currentStepIndex === 3;
    const accountsData = isBulk ? stepHelpers?.getStepData(0) || {} : {};
    
    const { consumerId: loggedInConsumerId } = getLoginDataFromStorage() || {};
    const consumerId = isSubAccount && urlConsumerId ? urlConsumerId : loggedInConsumerId;
    const consumerIds = isBulk 
      ? (accountsData.selectedAccounts || []).map((acc: any) => acc.id || acc) 
      : (consumerId ? [consumerId] : []);
    
    const payload: any = {
      remote_utility_id: remoteUtilityId,
      request_type: requestType,
      utility_support_request: Number(selectedId),
      consumer_ids: consumerIds,
      source: 1,
      payment_data: {
          payment_type: 1,
          payment_amount: rawPrice || "0",
          payment_mode: paymentMethod === 'online' ? 1 : 2,
          payment_pay_type: 0,
          status: "CREDIT",
          payment_received_status: 3,
          source:1
      }
    };

    const mutationOptions = {
     onSuccess: () => {
       if (onNext) onNext();
     },
     onError: (error: any) => {
       console.error("Failed to submit request", error);
     }
   };

   if (isBulk) {
     createBulkServiceRequest(payload, mutationOptions);
   } else {
     createBulkServiceRequest(payload, mutationOptions);
   }
 };

 return (
 <div className="space-y-8">
  <SelectedAccountsBanner stepHelpers={stepHelpers} />
  <div>
  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
   <CreditCard className="w-5 h-5" />
   {t("Review & Payment")}
  </h3>
  <p className="text-sm text-gray-500 mt-1">{t("Review your service request details and select payment method")}</p>
  </div>

  <div className="space-y-3">
  <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
   {t("Service Information")}
  </h4>
  {isLoading ? (
   <div className="border border-gray-200 rounded-xl p-6 bg-white text-center text-sm text-gray-500 animate-pulse">
    {t("Loading service details...")}
   </div>
  ) : selectedService ? (
  <div className="border border-gray-200 rounded-xl p-6 bg-white space-y-6">
   <div className="grid grid-cols-3 gap-4 items-start">
   <div>
    <div className="text-[10px] font-semibold text-gray-400 tracking-wider mb-1">{t("Service Name")}</div>
    <div className="font-semibold text-gray-900">{selectedService.name}</div>
   </div>
   <div>
    <div className="text-[10px] font-semibold text-gray-400 tracking-wider mb-2">{t("Utility Service")}</div>
    <div className="flex flex-wrap gap-1">
      {utilityServices.map((us: string, i: number) => (
        <span key={i} className="px-3 py-1 rounded-full text-xs font-medium tracking-wide bg-blue-100 text-blue-800 border border-blue-200 uppercase">{us}</span>
      ))}
    </div>
   </div>
   <div className="text-right">
    <div className="text-[10px] font-semibold text-gray-400 tracking-wider mb-1">{t("Service Charges")}</div>
    <div className="text-2xl font-bold text-blue-600 leading-none mb-1">{priceDisplay}</div>
    <div className="text-xs text-gray-500">{t("Base service fee")}</div>
   </div>
   </div>
   
   <div>
   <div className="text-[10px] font-semibold text-gray-400 tracking-wider mb-1">{t("Description")}</div>
   <p className="text-sm text-gray-700">{selectedService.longDescription || t("No description available.")}</p>
   </div>

   <div className="grid grid-cols-2 gap-6 pt-6 border-t border-gray-100">
   <div>
    <div className="text-[10px] font-semibold text-gray-400 tracking-wider mb-1">{t("Preferred Date")}</div>
    <div className="text-sm font-medium text-gray-900">{preferredDate}</div>
   </div>
   <div>
    <div className="text-[10px] font-semibold text-gray-400 tracking-wider mb-1">{t("Preferred Time")}</div>
    <div className="text-sm font-medium text-gray-900">{preferredTime}</div>
   </div>
   </div>
  </div>
  ) : (
   <div className="border border-gray-200 rounded-xl p-6 bg-white text-center text-sm text-gray-500">
    {t("No service selected.")}
   </div>
  )}
  </div>

  <div className="space-y-3">
  <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
   {t("Payment Method")}
   <Info className="w-4 h-4 text-gray-400" />
  </h4>
  
  <RadioGroup 
   value={paymentMethod}
   onValueChange={updatePaymentMethod}
   className="space-y-3"
  >
   <label 
   className={`flex items-center p-4 border rounded-xl cursor-pointer transition-colors ${
    paymentMethod ==='online'?'border-blue-500 bg-blue-50/10 ring-1 ring-blue-500':'border-gray-200 bg-white hover:border-gray-300'
   }`}
   >
   <RadioGroupItem value="online" id="online" />
   <div className="ml-3 flex items-center gap-2">
    <CreditCard className="h-5 w-5 text-gray-500"/>
    <span className="text-sm font-semibold text-gray-900">{t("Online Payment")}</span>
   </div>
   </label>

   <label 
   className={`flex items-center p-4 border rounded-xl cursor-pointer transition-colors ${
    paymentMethod ==='completion'?'border-blue-500 bg-blue-50/10 ring-1 ring-blue-500':'border-gray-200 bg-white hover:border-gray-300'
   }`}
   >
   <RadioGroupItem value="completion" id="completion" />
   <div className="ml-3">
    <span className="text-sm font-semibold text-gray-900">{t("On Service Completion")}</span>
   </div>
   </label>
  </RadioGroup>

  <div className="flex items-center gap-1.5 mt-4 text-xs font-medium text-blue-600">
   <Lock className="w-3.5 h-3.5"/>
   {t("Secure payment processing with 256-bit SSL encryption")}
  </div>
  </div>

  <div className="flex justify-between pt-6 border-t border-gray-200">
  <Button
   variant="outline"
   onClick={onPrevious}
   disabled={isSubmitting}
  >
   {t("Previous")}
  </Button>
  <Button
   onClick={handleSubmit}
   disabled={isSubmitting}
  >
   {isSubmitting ? t("Submitting...") : t("Submit Request")}
  </Button>
  </div>
 </div>
 );
};
